import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { asyncScheduler, map, observeOn, of, switchMap, withLatestFrom } from "rxjs";
import { redactionEditorActions } from "./redaction-editor.actions";
import { DocViewerStore } from "@store/legacy/doc-viewer.store";
import { RedactionService } from "@services/redaction.service";
import { RedactionAnnotationEdit } from "src/app/components/review/redaction-annotation/redaction-annotation-edit.model";
import { Store } from "@ngrx/store";
import { RedactionAnnotationRequestSdk } from "@sdks/redaction-annotation-request.sdk";
import { RedactionAnnotationCreateImageRequest } from "src/app/components/review/redaction-annotation/redaction-annotation-create-image-request.model";
import { RedactionAnnotationCreatePdfRequest } from "src/app/components/review/redaction-annotation/redaction-annotation-create-pdf-request.model";
import { DocViewerActions } from "../doc-viewer.actions";
import { allRedactions, currentDocTotalPageCount, currentRedactionEditor } from "@store/doc-viewer/redaction-editor/redaction-editor.selectors";
import { delay, filter } from "rxjs/operators";
import { ReviewSdk } from "@sdks/review.sdk";
import { LifeCycleEdit } from "../../../components/review/redaction-annotation/life-cycle-edit.enum";
import { cloneDeep } from "lodash-es";
import { FileService } from "@services/file.service";
import { FileType } from "@models/file-type";
import { PanelActions } from "../panel/panel.actions";
import { FileDownloadRequestResource } from "@models/resource/file-download-request.resource";
import { FileSdk } from "@sdks/file.sdk";
import { DocService } from "@services/doc.service";
import { RedactionStatus } from "@models/redaction-status.enum";
import { RedactionAnnotationsClient } from "@clients/api.client";

@Injectable()
export class RedactionEditorEffects {

	public docViewerInit$ = createEffect(() => this.actions$.pipe(
		ofType(DocViewerActions.loaded),
		switchMap(() => this.docViewerStore.currentDocument$),
		map((doc) => redactionEditorActions.initialize({
			docId: doc.docId,
			status: doc.status.redactionStatus,
			isProcessing: doc.status.isRedactionProcessing,
			totalPages: doc.common.pageCount
		}))
	));

	public initPage$ = createEffect(() => this.actions$.pipe(
		ofType(redactionEditorActions.initialize),
		map(() => redactionEditorActions.goToPage({ pageNum: 1 })),
	));

	public initRedactions$ = createEffect(() => this.actions$.pipe(
		ofType(redactionEditorActions.initialize),
		map((props) => redactionEditorActions.getRedactions({ docId: props.docId }))
	));

	public initColors$ = createEffect(() => this.actions$.pipe(
		ofType(redactionEditorActions.initialize),
		map(() => redactionEditorActions.getRedactionColors()),
	));

	public initFileList$ = createEffect(() => this.actions$.pipe(
		ofType(redactionEditorActions.initialize),
		withLatestFrom(this.docViewerStore.currentDocument$),
		switchMap(([, doc]) => {
			return this.fileService.getActiveFileIdsByDocIdAndType(doc.docId, FileType.RedactionImage).pipe(
				map((list) => {
					const structuredList = {};
					list.forEach(x => structuredList[x.rank] = x.fileId);
					return redactionEditorActions.setFileIdList({ items: structuredList })
				})
			);
		}),
	));

	public pollStatus$ = createEffect(() => {
		return this.actions$.pipe(
			observeOn(asyncScheduler),
			ofType(redactionEditorActions.processingStarted),
			withLatestFrom(this.docViewerStore.currentDocument$),
			delay(1000),
			switchMap(([action, doc]) => this.reviewSdk.getDocumentReviewState$(doc.docId).pipe(
				switchMap((response) => {
					const document = response?.document;
					if (document?.status?.isRedactionProcessing) {
						return of(action);
					}
					return of(redactionEditorActions.processingStopped({ status: document.status.redactionStatus }))
				})
			))
		)
	});

	public getRedactionsAfterProcessing$ = createEffect(() => {
		return this.actions$.pipe(
			observeOn(asyncScheduler),
			ofType(redactionEditorActions.processingStopped),
			withLatestFrom(this.docViewerStore.currentDocument$),
			switchMap(([, doc]) => of(redactionEditorActions.getRedactions({ docId: doc.docId })))
		)
	});

	public getRedactionColors$ = createEffect(() => this.actions$.pipe(
		ofType(redactionEditorActions.getRedactionColors),
		switchMap(() => this.redactionService.getRedactionColors('redaction').pipe(
			switchMap((colors) => of(redactionEditorActions.redactionColorsLoaded({ colors: colors })))
		))
	));

	public getRedaction$ = createEffect(() => this.actions$.pipe(
		ofType(redactionEditorActions.getRedactions),
		switchMap((props) => this.redactionService.getRedactions(props.docId).pipe(
			switchMap((redactions) => {
				const edits = redactions.map((e, i) => {
					return new RedactionAnnotationEdit(e, i);
				});
				return of(redactionEditorActions.redactionsLoaded({ redactions: edits }))
			})
		))
	));

	public createImage$ = createEffect(() => this.actions$.pipe(
		ofType(redactionEditorActions.createImage),
		withLatestFrom(this.docViewerStore.currentDocument$),
		switchMap(([, doc]) => {
			const createImageRequest = new RedactionAnnotationCreateImageRequest(doc.docId, true);
			return this.requestSdk.createImageRequest(createImageRequest).pipe(map(() => {
				return redactionEditorActions.processingStarted();
			}));
		}),
	));

	public docViewerSave$ = createEffect(() => this.actions$.pipe(
		ofType(DocViewerActions.saveRequested),
		withLatestFrom(this.store.select(currentRedactionEditor())),
		filter(([, editor]) => editor.hasUnsavedRedactionChanges),
		map(() => redactionEditorActions.createRedactedPDF())
	));

	public createRedactedPdf$ = createEffect(() => this.actions$.pipe(
		ofType(redactionEditorActions.createRedactedPDF),
		withLatestFrom(this.docViewerStore.currentDocument$, this.store.select(allRedactions)),
		switchMap(([, doc, sourceRedactions]) => {
			const redactions = cloneDeep(sourceRedactions).map((x) => {
				if (x.status === LifeCycleEdit.Created) {
					x.id = null;
					x.docId = doc.docId;
				}
				return x;
			})
			const createRedactedPdfRequest = new RedactionAnnotationCreatePdfRequest(doc.docId, true, redactions);
			return this.requestSdk.applyEditsRequest(createRedactedPdfRequest).pipe(
				switchMap((response) => {
					return response ? of(redactionEditorActions.processingStarted()) : of({ type: 'noop' });
				})
			);
		})
	));

	public viewRedactedPdfFile$ = createEffect(() => this.actions$.pipe(
		ofType(redactionEditorActions.viewRedactedPDFFile),
		switchMap((props) => {
			const fileDownloadRequest: FileDownloadRequestResource = {
				docId: props.docId,
				fileType: 9
			};
			return this.fileSdk.getFileDownloadUrl(fileDownloadRequest).pipe(
				switchMap((response) => {
					window.open(response.url, '_blank');
					return of({ type: 'noop' });
				})
			);
		})
	));

	public viewNativeRedactedFile$ = createEffect(() => this.actions$.pipe(
		ofType(redactionEditorActions.viewNativeRedactedFile),
		switchMap((props) => {
			const fileDownloadRequest: FileDownloadRequestResource = {
				docId: props.docId,
				fileType: 17
			};
			return this.fileSdk.getFileDownloadUrl(fileDownloadRequest).pipe(
				switchMap((response) => {
					window.open(response.url, '_blank');
					return of({ type: 'noop' });
				})
			);
		})
	));

	public resetRedactions$ = createEffect(() => this.actions$.pipe(
		ofType(redactionEditorActions.resetRedactionsForCurrentDoc),
		switchMap((props) => this.docService.delete(FileType.RedactedPdf, props.docId).pipe(
			withLatestFrom(this.docViewerStore.currentDocument$),
			switchMap(([, doc]) => {
				return of(redactionEditorActions.getRedactionStatus({ docId: doc.docId }))
			})
		))
	));

	public resetNativeRedactedFile$ = createEffect(() => this.actions$.pipe(
		ofType(redactionEditorActions.resetNativeRedactedFileForCurrentDoc),
		switchMap((props) => this.redactionAnnotationsClient.deleteNativeRedaction$(props.docId).pipe(
			withLatestFrom(this.docViewerStore.currentDocument$),
			switchMap(([, doc]) => {
				return of(redactionEditorActions.getRedactionStatus({ docId: doc.docId }))
			})
		))
	));

	public getRedactionStatus$ = createEffect(() => this.actions$.pipe(
		ofType(redactionEditorActions.getRedactionStatus),
		switchMap((props) => this.reviewSdk.getDocumentReviewState$(props.docId).pipe(
			switchMap((response) => {
				const document = response?.document;
				return of(redactionEditorActions.redactionStatusLoaded({ status: document.status.redactionStatus }));
			})
		))
	));

	public statusLoaded$ = createEffect(() => this.actions$.pipe(
		ofType(redactionEditorActions.redactionStatusLoaded),
		withLatestFrom(this.docViewerStore.currentDocument$),
		switchMap(([, doc]) => of(redactionEditorActions.getRedactions({ docId: doc.docId })))
	));

	public syncPageExpander$ = createEffect(() => this.actions$.pipe(
		ofType(PanelActions.expandPanel),
		filter((props) => props.panelId.startsWith('expandable-redaction-panel-page-')),
		withLatestFrom(this.store.select(currentDocTotalPageCount())),
		switchMap(([props, totalPageCount]) => {
			const numericPart = props.panelId.match(/\d+/);
			const pageNumber = numericPart ? parseInt(numericPart[0], 10) : NaN;
			if (pageNumber && (pageNumber >= 1 || pageNumber <= totalPageCount))
				return of(redactionEditorActions.goToPage({ pageNum: pageNumber }));
			return of({ type: 'noop' });
		})
	));

	public handleStatus$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(redactionEditorActions.redactionStatusLoaded, redactionEditorActions.initialize),
			switchMap((props) => {
				if ((props.status & RedactionStatus.ImagesCreationRequested)
					|| (props.status & RedactionStatus.ImagesProcessing)
					|| (props.status & RedactionStatus.PdfCreationRequested)
					|| (props.status & RedactionStatus.PdfProcessing)) {
					return of(redactionEditorActions.processingStarted());
				}
				return of({ type: 'noop' });
			})
		)
	});

	constructor(
		private store: Store,
		private actions$: Actions,
		private requestSdk: RedactionAnnotationRequestSdk,
		private docViewerStore: DocViewerStore,
		private redactionService: RedactionService,
		private reviewSdk: ReviewSdk,
		private fileService: FileService,
		private fileSdk: FileSdk,
		private docService: DocService,
		private redactionAnnotationsClient: RedactionAnnotationsClient
	) {
	}
}
